import { Money } from '@principle-theorem/accounting';
import {
  Transaction,
  TransactionOperators,
  getBaseTransaction,
} from '@principle-theorem/principle-core';
import {
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  isHealthPointTransactionExtendedData,
  type IHealthPointTransactionExtendedData,
  type IInvoice,
  type IPractice,
  type ITransaction,
  IHealthcareClaim,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  toInt,
  type WithRef,
} from '@principle-theorem/shared';
import {
  isHealthPointTransactionCompleteCallbackData,
  type IBaseTransactionCompleteCallbackData,
  type IHealthPointClaimRequestParams,
  type IHealthPointTransactionErrorCallback,
  type IHealthPointTransactionSuccessBaseCallbackData,
} from '@principle-theorem/tyro';
import { differenceBy, first, sum } from 'lodash';
import { toTyroTransaction } from '../base-transaction';

export async function toHealthPointTransaction(
  invoice: WithRef<IInvoice>,
  practiceRef: DocumentReference<IPractice>,
  claim: IHealthcareClaim,
  request: IHealthPointClaimRequestParams,
  response: IBaseTransactionCompleteCallbackData
): Promise<ITransaction<IHealthPointTransactionExtendedData>> {
  const baseTyroTransaction = await toTyroTransaction(
    TransactionProvider.TyroHealthPoint,
    invoice,
    practiceRef,
    response
  );
  return Transaction.init({
    ...baseTyroTransaction,
    amount: Money.fromCents(getBenefitAmount(response)),
    extendedData: {
      request,
      response,
    },
    attributedTo: claim.practitioner.ref,
  });
}

function getBenefitAmount(
  response: IBaseTransactionCompleteCallbackData
): number {
  if (!isHealthPointTransactionCompleteCallbackData(response)) {
    return 0;
  }
  return response.healthpointTotalBenefitAmount
    ? toInt(response.healthpointTotalBenefitAmount)
    : assumeBenefitAmount(response);
}

export async function toHealthPointCancelTransaction(
  invoice: WithRef<IInvoice>,
  practiceRef: DocumentReference<IPractice>,
  claim: IHealthcareClaim | undefined,
  request: IHealthPointClaimRequestParams,
  response: IHealthPointTransactionSuccessBaseCallbackData,
  initialTransaction: WithRef<ITransaction<IHealthPointTransactionExtendedData>>
): Promise<ITransaction<IHealthPointTransactionExtendedData>> {
  const benefitAmount = response.healthpointTotalBenefitAmount
    ? toInt(response.healthpointTotalBenefitAmount)
    : assumeBenefitAmount(response);
  const base = await toTyroTransaction(
    TransactionProvider.TyroHealthPoint,
    invoice,
    practiceRef,
    response,
    TransactionType.Outgoing
  );
  return Transaction.init({
    ...base,
    reference: initialTransaction.reference,
    amount: Money.fromCents(benefitAmount),
    extendedData: {
      request,
      response,
    },
    attributedTo: claim?.practitioner.ref ?? initialTransaction.attributedTo,
  });
}

export async function toHealthPointPendingTransaction(
  invoice: WithRef<IInvoice>,
  practiceRef: DocumentReference<IPractice>,
  claim: IHealthcareClaim,
  request: IHealthPointClaimRequestParams,
  response: IHealthPointTransactionErrorCallback
): Promise<ITransaction<IHealthPointTransactionExtendedData>> {
  const base = await getBaseTransaction(invoice, TransactionType.Incoming);
  return Transaction.init({
    ...base,
    reference: response.transactionId,
    status: TransactionStatus.Pending,
    provider: TransactionProvider.TyroHealthPoint,
    practiceRef,
    amount: Money.fromCents(toInt(request.totalClaimAmount)),
    extendedData: {
      request,
      response,
    },
    attributedTo: claim.practitioner.ref,
  });
}

export async function toHealthPointPendingCancelTransaction(
  invoice: WithRef<IInvoice>,
  practiceRef: DocumentReference<IPractice>,
  claim: IHealthcareClaim | undefined,
  request: IHealthPointClaimRequestParams,
  response: IHealthPointTransactionErrorCallback,
  initialTransaction: WithRef<ITransaction<IHealthPointTransactionExtendedData>>
): Promise<ITransaction<IHealthPointTransactionExtendedData>> {
  const base = await getBaseTransaction(invoice, TransactionType.Outgoing);
  return Transaction.init({
    ...base,
    reference: initialTransaction.reference,
    status: TransactionStatus.Pending,
    provider: TransactionProvider.TyroHealthPoint,
    practiceRef,
    amount: initialTransaction.amount,
    extendedData: {
      request,
      response,
    },
    attributedTo: claim?.practitioner.ref ?? initialTransaction.attributedTo,
  });
}

function assumeBenefitAmount(
  data: IHealthPointTransactionSuccessBaseCallbackData
): number {
  const rebateAmounts = data.healthpointClaimItems.map((item) =>
    toInt(item.rebateAmount)
  );
  return sum(rebateAmounts);
}

function filterCompleteHealthPointClaim(
  transactions: WithRef<ITransaction>[]
): ITransaction<IHealthPointTransactionExtendedData>[] {
  return new TransactionOperators(transactions)
    .extendedDataGuard(isHealthPointTransactionExtendedData)
    .completed()
    .incoming()
    .result();
}

function filterCompleteHealthPointClaimCancels(
  transactions: WithRef<ITransaction>[]
): ITransaction<IHealthPointTransactionExtendedData>[] {
  return new TransactionOperators(transactions)
    .extendedDataGuard(isHealthPointTransactionExtendedData)
    .completed()
    .outgoing()
    .result();
}

export function findActiveHealthPointClaim(
  transactions: WithRef<ITransaction>[]
): ITransaction<IHealthPointTransactionExtendedData> | undefined {
  const cancelTransactions =
    filterCompleteHealthPointClaimCancels(transactions);
  const claimTransactions = filterCompleteHealthPointClaim(transactions);
  const activeClaims = differenceBy(
    claimTransactions,
    cancelTransactions,
    'transactionId'
  );
  return first(activeClaims);
}
